import axios from "axios";

import Config from "./Config";
import SettingsService from "./SettingsService";
import Memory from "./MemoryService";
import APIHelper from "./APIHelper";
import MODULE_COPY from "./ModuleCopy";

export const PILLARS_MEMORY_KEY = "brandPillars";

const promptsBeforeDisabling = (entry, nested) =>
    !nested || typeof entry.module_id === "number";

const toModule = (entry, nested = false) => {
    const copy = MODULE_COPY[entry.setting_name] ?? {};

    return {
        code: entry.setting_name,
        label: entry.label,
        book: entry.book ?? "store",
        type: entry.type,
        inverted: entry.inverted ?? false,
        active: entry.active ?? true,
        moduleID: entry.module_id ?? null,
        confirmDisable: promptsBeforeDisabling(entry, nested),
        options: (entry.options ?? []).map((option) => ({ ...option })),
        settings: (entry.sub_settings ?? []).map((subSetting) =>
            toModule(subSetting, true)
        ),
        confirm: copy.confirm,
        feedback: copy.feedback,
    };
};

const toPillars = (rows) => {
    if (!Array.isArray(rows)) {
        return false;
    }

    return rows.map((pillar) => ({
        code: pillar.code,
        label: pillar.label,
        modules: Array.isArray(pillar.modules)
            ? pillar.modules.map((entry) => toModule(entry))
            : [],
    }));
};

const collectSettingNames = (modules, names = new Set()) => {
    modules.forEach((module) => {
        names.add(module.code);
        collectSettingNames(module.settings, names);
    });

    return names;
};

let cancels = {};
const BrandService = {
    typeahead(search, organization = null) {
        if (typeof cancels["typeahead"] !== "undefined") {
            cancels["typeahead"].cancel(
                "Operation canceled due to new request."
            );
        }
        cancels["typeahead"] = axios.CancelToken.source();
        return axios
            .post(
                Config.API_URL + "brand/search",
                {
                    name: search,
                    organization_id: organization,
                    pdlt: SettingsService.Get("pdlt", false),
                },
                {
                    cancelToken: cancels["typeahead"].token,
                }
            )
            .then(APIHelper.returnItemsOrEmptyArray)
            .catch((error) => {
                if (
                    typeof error.message !== "undefined" &&
                    /Operation canceled/.test(error.message)
                ) {
                    return null;
                }
                return [];
            });
    },
    dailyVolume(id, website) {
        return axios
            .get(Config.API_URL + "brand/" + id + "/daily/" + website)
            .then(APIHelper.returnDataOrEmptyArray)
            .catch(APIHelper.returnEmptyArray);
    },
    merchants(id) {
        return axios
            .get(Config.API_URL + "brand/" + id + "/merchants")
            .then(APIHelper.returnPayload)
            .catch(APIHelper.returnFalse);
    },
    getSellingWebsites(id) {
        return axios
            .get(Config.API_URL + "brand/" + id + "/websites")
            .then(APIHelper.returnItemsOrEmptyArray)
            .catch(APIHelper.returnEmptyArray);
    },
    troubleshoot(id, websites) {
        return axios
            .post(Config.API_URL + "brand/" + id + "/troubleshoot", {
                websites,
            })
            .then((response) => APIHelper.returnAttribute(response, "websites"))
            .catch(APIHelper.returnFalse);
    },
    summary(id) {
        return axios
            .get(Config.API_URL + "brand/" + id + "/summary")
            .then(APIHelper.returnPayload)
            .catch(APIHelper.returnFalse);
    },
    products(id, extended = false) {
        return axios
            .get(
                Config.API_URL +
                    "brand/" +
                    id +
                    "/products" +
                    (extended ? "/extended" : "")
            )
            .then(APIHelper.returnItems)
            .catch(APIHelper.returnFalse);
    },
    trends(id) {
        return axios
            .get(Config.API_URL + "brand/" + id + "/trends")
            .then(APIHelper.returnPayload)
            .catch(APIHelper.returnFalse);
    },
    categories(id) {
        return axios
            .get(Config.API_URL + "brand/" + id + "/categories")
            .then((response) =>
                APIHelper.returnAttribute(response, "categories")
            )
            .catch(APIHelper.returnFalse);
    },
    productsDiscovery(id) {
        return axios
            .get(Config.API_URL + "brand/" + id + "/discovery")
            .then(APIHelper.returnPayload)
            .catch(APIHelper.returnFalse);
    },
    costs(id) {
        return axios
            .get(Config.API_URL + "brand/" + id + "/costs")
            .then(APIHelper.returnData)
            .catch(APIHelper.returnFalse);
    },
    violations(id) {
        return axios
            .get(Config.API_URL + "brand/" + id + "/violations")
            .then(APIHelper.returnItems)
            .catch(APIHelper.returnFalse);
    },
    /**
     * Retrieves and stores the Brand name in memory for rendering the Breadcrumbs of /brands
     * @param {int} id
     * @returns
     */
    breadcrumb(id) {
        return new Promise((resolve, reject) => {
            if (Memory.Has("brand/" + id)) {
                resolve(Memory.Get("brand/" + id));
            } else {
                axios
                    .get(Config.API_URL + "brand/" + id + "/name")
                    .then((response) => {
                        if (APIHelper.checkStatus(response)) {
                            Memory.Set("brand/" + id, response.data);
                            resolve(response.data);
                        }
                        reject();
                    })
                    .catch((error) => {
                        reject();
                    });
            }
        });
    },
    all() {
        return axios
            .get(Config.API_URL + "brand")
            .then(APIHelper.returnData)
            .catch(APIHelper.returnFalse);
    },
    get(id) {
        return axios
            .get(Config.API_URL + "brand/" + id)
            .then(APIHelper.returnData)
            .catch(APIHelper.returnFalse);
    },
    restrictions(id) {
        return axios
            .get(Config.API_URL + "brand/" + id + "/restrictions")
            .then(APIHelper.returnPayload)
            .catch(APIHelper.returnFalse);
    },
    addRestriction(id, website_id, mode) {
        return axios
            .put(
                Config.API_URL + "brand/" + id + "/restrictions/" + website_id,
                { mode }
            )
            .then(APIHelper.checkStatus)
            .catch(APIHelper.returnFalse);
    },
    addRestrictions(id, websites, mode) {
        return axios
            .put(Config.API_URL + "brand/" + id + "/restrictions", {
                mode,
                websites,
            })
            .then(APIHelper.returnPayload)
            .catch(APIHelper.returnFalse);
    },
    removeRestriction(id, website_id) {
        return axios
            .delete(
                Config.API_URL + "brand/" + id + "/restrictions/" + website_id
            )
            .then(APIHelper.checkStatus)
            .catch(APIHelper.returnFalse);
    },
    users(id) {
        return axios
            .get(Config.API_URL + "brand/" + id + "/users")
            .then((response) => APIHelper.returnAttribute(response, "users"))
            .catch(APIHelper.returnFalse);
    },
    update(id, changes) {
        return axios
            .put(Config.API_URL + "brand/" + id, changes)
            .then(APIHelper.returnData)
            .catch(APIHelper.returnFalse);
    },
    /**
     * @returns {Promise<Array|boolean>} mapped pillars, or false when unavailable
     */
    getPillars() {
        const cached = Memory.Get(PILLARS_MEMORY_KEY, null);
        if (cached !== null) {
            return cached.then(toPillars);
        }

        const rows = axios
            .get(Config.API_URL + "pillars")
            .then(APIHelper.returnRows)
            .catch(APIHelper.returnFalse)
            .then((payload) => {
                if (toPillars(payload) === false) {
                    Memory.Flush(PILLARS_MEMORY_KEY);
                }

                return payload;
            });

        Memory.Set(PILLARS_MEMORY_KEY, rows);
        return rows.then(toPillars);
    },
    /**
     * @param {Array} pillars mapped catalog
     * @returns {Array} setting names that have copy but no matching entry
     */
    unmatchedModuleCopy(pillars) {
        if (!Array.isArray(pillars)) {
            return [];
        }

        const present = collectSettingNames(
            pillars.flatMap((pillar) => pillar.modules)
        );

        return Object.keys(MODULE_COPY).filter((name) => !present.has(name));
    },
    new(name) {
        return axios
            .post(Config.API_URL + "brand", {
                name,
            })
            .then((response) => APIHelper.returnAttribute(response, "id"))
            .catch(APIHelper.returnFalse);
    },
    getSellingCountries(id) {
        return axios
            .get(Config.API_URL + "brand/" + id + "/countries")
            .then((response) =>
                APIHelper.returnAttribute(response, "countries")
            )
            .catch(APIHelper.returnFalse);
    },
};

export default BrandService;
